import type { Message, PollSummary } from '../../types/index.js';
import { buildQuery, type RequestFn } from '../http.js';

/** Messages and everything attached to them: reactions, pins, search, polls,
 *  task lists, comment threads and read receipts.
 *
 *  `generateNonce` is injected so the idempotency key generator can differ per
 *  runtime (the web and native cores wire their own; see {@link createApi}). */
export function makeMessagesApi(request: RequestFn, generateNonce: () => string) {
  return {
    listMessages: (
      channelId: string,
      opts: { before?: string; after?: string; limit?: number } = {},
    ) =>
      request<{ messages: Message[] }>(
        `/api/channels/${channelId}/messages${buildQuery({
          before: opts.before,
          after: opts.after,
          limit: opts.limit,
        })}`,
      ),
    listPinnedMessages: (channelId: string) =>
      request<{ messages: Message[] }>(`/api/channels/${channelId}/pinned`),
    /** Attachment-bearing messages only (newest first) — backs the profile
     *  "shared media / files / voice" tabs without paging the whole history. */
    listChannelAttachments: (
      channelId: string,
      opts: { before?: string; limit?: number } = {},
    ) =>
      request<{ messages: Message[] }>(
        `/api/channels/${channelId}/attachments${buildQuery({
          before: opts.before,
          limit: opts.limit,
        })}`,
      ),
    searchMessages: (channelId: string, query: string) =>
      request<{ messages: Message[] }>(
        `/api/channels/${channelId}/search?q=${encodeURIComponent(query)}`,
      ),
    sendMessage: (
      channelId: string,
      input: {
        content: string;
        replyToId?: string;
        mentions?: string[];
        uploadIds?: string[];
        documentUploadIds?: string[];
        forwardUploadIds?: string[];
        forwardFromMessageId?: string;
        clientNonce?: string;
      },
    ) =>
      request<Message>(`/api/channels/${channelId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          // Auto-generate a nonce if the caller didn't bring one. This is the
          // idempotency key the server uses to dedupe retries.
          clientNonce: input.clientNonce ?? generateNonce(),
        }),
      }),
    editMessage: (id: string, input: { content: string; mentions?: string[] }) =>
      request<Message>(`/api/messages/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    /** Delete a message. `scope: 'me'` hides it from just the caller (Telegram
     *  "delete for me"); the default `'everyone'` is the global delete for
     *  authors/moderators. */
    deleteMessage: (id: string, scope: 'me' | 'everyone' = 'everyone') =>
      request<void>(`/api/messages/${id}${scope === 'me' ? '?scope=me' : ''}`, { method: 'DELETE' }),
    pinMessage: (id: string) => request<Message>(`/api/messages/${id}/pin`, { method: 'POST' }),
    unpinMessage: (id: string) => request<Message>(`/api/messages/${id}/pin`, { method: 'DELETE' }),
    addReaction: (id: string, emoji: string) =>
      request<void>(`/api/messages/${id}/reactions/${encodeURIComponent(emoji)}`, {
        method: 'PUT',
      }),
    removeReaction: (id: string, emoji: string) =>
      request<void>(`/api/messages/${id}/reactions/${encodeURIComponent(emoji)}`, {
        method: 'DELETE',
      }),

    // ---------- polls ----------
    createPoll: (
      channelId: string,
      body: {
        question: string;
        options: string[];
        multiChoice?: boolean;
        anonymous?: boolean;
      },
    ) =>
      request<Message>(`/api/channels/${channelId}/polls`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // ---------- task lists (Telegram-style checklists) ----------
    createTaskList: (
      channelId: string,
      body: { title: string; items: string[]; allowComplete?: boolean; allowAdd?: boolean },
    ) =>
      request<Message>(`/api/channels/${channelId}/tasks`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    toggleTaskItem: (listId: string, itemId: string) =>
      request<Message>(`/api/tasks/${listId}/items/${itemId}`, { method: 'PUT' }),
    addTaskItem: (listId: string, label: string) =>
      request<Message>(`/api/tasks/${listId}/items`, {
        method: 'POST',
        body: JSON.stringify({ label }),
      }),

    votePoll: (pollId: string, optionIds: string[]) =>
      request<PollSummary>(`/api/polls/${pollId}/vote`, {
        method: 'PUT',
        body: JSON.stringify({ optionIds }),
      }),
    closePoll: (pollId: string) =>
      request<PollSummary>(`/api/polls/${pollId}/close`, {
        method: 'PATCH',
      }),

    // ---------- message threads (Telegram-style comments) ----------
    listThreadComments: (messageId: string) =>
      request<{ comments: Message[] }>(`/api/messages/${messageId}/thread`),
    postThreadComment: (
      messageId: string,
      input: {
        content: string;
        replyToId?: string;
        mentions?: string[];
        uploadIds?: string[];
        clientNonce?: string;
      },
    ) =>
      request<Message>(`/api/messages/${messageId}/thread`, {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          clientNonce: input.clientNonce ?? generateNonce(),
        }),
      }),
    setThreadClosed: (messageId: string, closed: boolean) =>
      request<Message>(`/api/messages/${messageId}/thread`, {
        method: 'PATCH',
        body: JSON.stringify({ closed }),
      }),

    // ---------- read receipts ----------
    // keepalive: the last read mark is often sent as the tab closes or the
    // user navigates away; without it the browser may cancel the request.
    markRead: (channelId: string, messageId: string) =>
      request<void>(`/api/channels/${channelId}/read`, {
        method: 'POST',
        body: JSON.stringify({ messageId }),
        keepalive: true,
      }),
    listReadMarkers: (channelId: string) =>
      request<{ markers: { userId: string; messageId: string; updatedAt: string }[] }>(
        `/api/channels/${channelId}/read`,
      ),
  };
}
